/**
 * Which Mapbox style a map renders with. A full-size map follows the theme — a
 * light map on a dark page is a bright rectangle in the middle of the screen —
 * and a small inset one does not, because at that size the detail is the whole
 * point. See each function.
 *
 * Public rather than `Internal/` because the dashboard's ShipmentMap needs the
 * same pair: it's a decorative full-bleed route strip with its own arc, pins and
 * animated marker, so it can't render through `Map` — but its styles must not
 * drift from these. They did, and the older light style it was pinned to is why
 * that map was a globe in dark mode and flat in light.
 */

/**
 * Mapbox GL style URL for the current theme — the full-size, interactive maps.
 *
 * Dark is `dark-v11` on purpose. Its land and water sit at nearly the same
 * value, which is low contrast — and that's the look we want at full size: an
 * almost-black globe against the page. The Standard style's `night` lighting was
 * tried instead and is more legible, but it reads as a washed blue-grey ball
 * with a bright rim. Don't "fix" the contrast here.
 */
export function mapboxStyleUrl(isDark: boolean): string {
  return isDark
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/streets-v12";
}

/**
 * Mapbox Static API style path segment. The colour style, in both themes, and
 * it takes no theme for that reason.
 *
 * Every static map we render is a small inset beside an address, and an inset is
 * read for its detail: which streets, which side of the highway, how close to
 * the water. The dark style throws nearly all of that away, which is the point
 * at full size (see `mapboxStyleUrl`) and useless at 384x160. So these stay
 * colour and accept being a bright rectangle on a dark page.
 *
 * Note the Static Images API only serves the older "classic" styles — it 400s on
 * Standard — so this can never simply follow `mapboxStyleUrl` if that ever moves
 * to a modern style.
 */
export function mapboxStaticStylePath(): string {
  return "mapbox/streets-v12";
}
